import { ChartData, ChartDataset } from 'chart.js';
import { useEffect, useState } from 'react';

import { AiUnitHourlyCummulativeRequest_Type } from '../../../../admin-center/gen/aiUnitHourly/aiUnitHourly_pb';
import { useAdminCenterService } from '../../../../admin-center/hooks';
import {
  FlatRawChartData,
  formatDateLocal,
  NestedRawChartData,
  transformFlatDataForCharts,
  transformNestedDataForCharts,
} from '../../common/utils';
import { aiUnitHourlyComponentsColorMap, aiUnitHourlyComponentsLabelsMap } from './utils';

export const useAIUnitHourlyCumulativeData = (
  startDate: Date | null,
  endDate: Date | null,
  showCumulativeComponentData: boolean
) => {
  const service = useAdminCenterService();
  const [augmentedData, setAugmentedData] = useState<ChartData<'line'>>({ labels: [], datasets: [] });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [threshold, setThreshold] = useState<number | null>(null);
  const [firstDate, setFirstDate] = useState<string | null>(null);
  const [lastDate, setLastDate] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const [totalUsage, setTotalUsage] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [dateRangeResponse, hourlyResponse, thresholdResponse] = await Promise.all([
          service.getAiUnitHourlyDateRange({}),
          service.getHourlyAiUnitCummulativeUsage({
            startDate: startDate ? formatDateLocal(startDate) : '',
            endDate: endDate ? formatDateLocal(endDate) : '',
            type: showCumulativeComponentData
              ? AiUnitHourlyCummulativeRequest_Type.COMPONENT
              : AiUnitHourlyCummulativeRequest_Type.SUMMARY,
          }),
          service.getAiUsageThresholds({
            startDate: startDate ? formatDateLocal(startDate) : '',
            endDate: endDate ? formatDateLocal(endDate) : '',
          }),
        ]);

        setFirstDate(dateRangeResponse.firstDate || null);
        setLastDate(dateRangeResponse.lastDate || null);

        const currentThreshold = thresholdResponse.threshold || null;
        setThreshold(currentThreshold);

        if ('componentResponse' in hourlyResponse && hourlyResponse.componentResponse) {
          const usageData = hourlyResponse.componentResponse.data;

          const transformedEvents = transformNestedDataForCharts(
            usageData as NestedRawChartData,
            aiUnitHourlyComponentsLabelsMap,
            aiUnitHourlyComponentsColorMap
          );

          const stackedDatasets =
            transformedEvents?.datasets?.map((dataset) => ({
              ...dataset,
              stack: 'components',
              fill: 'origin',
            })) || [];

          const lastIndex = (transformedEvents?.labels?.length || 1) - 1;
          const initialTotal = stackedDatasets.reduce((sum: number, dataset: ChartDataset) => {
            const lastValue = dataset.data[lastIndex] || 0;
            return sum + (typeof lastValue === 'number' ? lastValue : 0);
          }, 0);

          setTotalUsage(initialTotal);

          const thresholdLine = {
            label: 'Threshold',
            data: transformedEvents?.labels?.map(() => currentThreshold),
            borderColor: 'green',
            borderWidth: 1,
            borderDash: [5, 5],
            fill: false,
            pointRadius: 0,
            hoverRadius: 0,
            stack: 'threshold',
          };

          const newAugmentedData = {
            ...transformedEvents,
            datasets: [...stackedDatasets, thresholdLine],
          };

          setAugmentedData(newAugmentedData);
          setIsEmpty(!usageData || Object.keys(usageData).length === 0);
        } else if ('summaryResponse' in hourlyResponse && hourlyResponse.summaryResponse) {
          const usageData = hourlyResponse.summaryResponse.data;
          const usageValues: number[] = usageData ? Object.values(usageData) : [];
          const currentTotalUsage = usageValues[usageValues.length - 1] || 0;
          setTotalUsage(currentTotalUsage);

          const transformedEvents = transformFlatDataForCharts(
            usageData as FlatRawChartData,
            'Cumulative Sum',
            '#6389e1'
          );

          const thresholdLine = {
            label: 'Threshold',
            data: transformedEvents?.labels?.map(() => currentThreshold),
            borderColor: 'green',
            borderWidth: 1,
            borderDash: [5, 5],
            fill: false,
            pointRadius: 0,
            hoverRadius: 0,
          };

          const newAugmentedData = {
            ...transformedEvents,
            datasets: [...(transformedEvents?.datasets || []), thresholdLine],
          };

          setAugmentedData(newAugmentedData);
          setIsEmpty(!usageData || Object.keys(usageData).length === 0);
        }
      } catch (error) {
        setError('Error fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, service, showCumulativeComponentData]);

  return { augmentedData, loading, error, threshold, totalUsage, firstDate, lastDate, isEmpty };
};

export const useAIUnitHourlyComponentData = (startDate: Date | null, endDate: Date | null) => {
  const service = useAdminCenterService();
  const [componentData, setComponentData] = useState<ChartData<'line'>>({ labels: [], datasets: [] });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [threshold, setThreshold] = useState<number | null>(null);
  const [firstDate, setFirstDate] = useState<string | null>(null);
  const [lastDate, setLastDate] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [dateRangeResponse, hourlyResponse, thresholdResponse] = await Promise.all([
          service.getAiUnitHourlyDateRange({}),
          service.getHourlyAiUnitComponentUsage({
            startDate: startDate ? formatDateLocal(startDate) : '',
            endDate: endDate ? formatDateLocal(endDate) : '',
          }),
          service.getAiUsageThresholds({
            startDate: startDate ? formatDateLocal(startDate) : '',
            endDate: endDate ? formatDateLocal(endDate) : '',
          }),
        ]);

        const usageData = hourlyResponse.data;
        setFirstDate(dateRangeResponse.firstDate || null);
        setLastDate(dateRangeResponse.lastDate || null);
        const transformedEvents = transformNestedDataForCharts(
          usageData as NestedRawChartData,
          aiUnitHourlyComponentsLabelsMap,
          aiUnitHourlyComponentsColorMap
        );
        setComponentData(transformedEvents);

        setThreshold(thresholdResponse.threshold || null);
        setIsEmpty(!usageData || Object.keys(usageData).length === 0);
      } catch (error) {
        setError('Error fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, service]);

  return { componentData, loading, error, threshold, firstDate, lastDate, isEmpty };
};
